/**
 * Candidate portal login page.
 *
 * Two-step:
 *   1. Email + password -> POST /portal/login/begin returns a challenge_id
 *      (and triggers an OTP delivery via SMTP / future SMS).
 *   2. Code + challenge_id -> POST /portal/login/verify sets the session
 *      cookie. We then navigate to /portal/.
 *
 * The backend always returns the same begin response whether the email
 * is recognised or not (DL-038, anti-enumeration). Don't infer "user
 * exists" from a successful begin.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { usePortalAuth } from './auth';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { InlineAlert } from '../components/ui/States';
import { Wordmark } from '../components/Wordmark';

// Default OTP lifetime when the server doesn't report one (older
// backends or anti-enumeration responses). Matches the server-side
// candidate_otp_ttl_minutes default of 10 minutes.
const _DEFAULT_OTP_TTL_SECONDS = 10 * 60;

function formatRemaining(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

type Step = 'credentials' | 'code';

export function PortalLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginBegin, loginVerify, refresh } = usePortalAuth();

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from
      ?.pathname ?? '/portal/';

  // Flash banner — shown when arriving from /portal/reset-password etc.
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // OTP countdown. `expiresAt` is an absolute timestamp (ms-since-epoch)
  // so the timer survives tab-throttling and is unaffected by setInterval
  // drift. `secondsLeft` is re-derived every tick.
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (expiresAt === null) {
      setSecondsLeft(0);
      return;
    }
    function tick() {
      const remaining = Math.max(
        0,
        Math.ceil(((expiresAt ?? 0) - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
    }
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  async function handleResend() {
    if (!email || !password) {
      // Credentials cleared on a back-nav. Send them to the credentials
      // step rather than firing a half-formed request.
      setError('Please sign in again to get a new code.');
      setStep('credentials');
      return;
    }
    setError(null);
    setIsResending(true);
    try {
      const resp = await loginBegin({ email, password });
      if (resp.challenge_id) {
        setChallengeId(resp.challenge_id);
        setCode('');
        setExpiresAt(
          Date.now() +
            (resp.expires_in_seconds ?? _DEFAULT_OTP_TTL_SECONDS) * 1000,
        );
        setInfo('We sent a new verification code. Check your inbox.');
      } else {
        // No challenge — server treated this as a trusted-device or
        // failed-credentials path. Bounce back to the credentials step
        // so the candidate isn't stuck with a stale challenge_id.
        setError('Could not request a new code. Please sign in again.');
        setStep('credentials');
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not request a new code. Try again.',
      );
    } finally {
      setIsResending(false);
    }
  }

  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const resp = await loginBegin({ email, password });
      if (resp.challenge_id) {
        // Standard path: OTP step.
        setChallengeId(resp.challenge_id);
        setExpiresAt(
          Date.now() +
            (resp.expires_in_seconds ?? _DEFAULT_OTP_TTL_SECONDS) * 1000,
        );
        setInfo(resp.message);
        setStep('code');
        return;
      }

      // No challenge_id can mean two things, both designed to be
      // indistinguishable to the client (DL-038, anti-enumeration):
      //   (a) Trusted-device path — the backend set a session cookie and
      //       the candidate is now signed in.
      //   (b) Wrong credentials — no cookie was set.
      // We resolve which one it is by asking /portal/status. If we're
      // authenticated, navigate; otherwise show the same generic error
      // we would for any auth failure (no email-existence leak).
      await refresh();
      const statusResp = await fetch('/portal/status', {
        credentials: 'include',
      });
      if (statusResp.ok) {
        navigate(from, { replace: true });
      } else {
        setError('Email or password not recognised.');
      }
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) {
        setError('Email or password not recognised.');
      } else {
        setError(err instanceof Error ? err.message : 'Could not sign in. Try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!challengeId) {
      setError('Login session expired. Please start over.');
      setStep('credentials');
      return;
    }
    setIsLoading(true);
    try {
      await loginVerify({ challenge_id: challengeId, code, trust_device: trustDevice });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify the code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-[var(--color-bg)] lg:grid-cols-[1.05fr_1fr]">
      {/* ── Left panel: brand gradient hero (teal-navy variant) ──────── */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-teal-navy px-12 py-14 text-white lg:flex xl:px-20 xl:py-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 70% 30%, white 0, transparent 40%), radial-gradient(circle at 30% 70%, white 0, transparent 40%)',
          }}
        />

        <div className="relative z-10">
          <Wordmark size={28} inverse />
        </div>

        <div className="relative z-10 max-w-[520px]">
          <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70 mb-6">
            Candidate portal
          </p>
          <h1 className="display text-white text-[60px] xl:text-[72px]">
            A fair&nbsp;hearing
            <br />
            for your <span className="text-[var(--color-success)]">evidence.</span>
          </h1>
          <p className="mt-7 max-w-[420px] font-sans text-[15px] leading-relaxed text-white/80">
            Prism evaluates your responses against a published competency
            framework. Every result is reviewed by a human assessor before
            it is shared with the hiring team.
          </p>
        </div>

        <a
          href="mailto:hr@mediantlabs.com?subject=Prism%20candidate%20login"
          className="relative z-10 inline-flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline"
        >
          <span>Need help?</span>
          <span aria-hidden="true">·</span>
          <span>Contact your assessor</span>
        </a>
      </aside>

      {/* ── Right panel: form ─────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-[420px] prism-enter">
          <div className="mb-10 lg:hidden">
            <Wordmark size={28} />
          </div>

          {step === 'credentials' && (
            <>
              <p className="eyebrow mb-3">Sign in</p>
              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
                Welcome.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                Use the credentials your assessor sent you. The first time
                you sign in, you will be asked to set a new password.
              </p>

              {flash && (
                <div className="mt-6">
                  <InlineAlert variant="success">{flash}</InlineAlert>
                </div>
              )}

              <form onSubmit={handleCredentials} noValidate className="mt-10 space-y-6">
                <Input
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <div>
                  <Input
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                  <div className="mt-2 text-right">
                    <Link
                      to="/portal/forgot-password"
                      className="font-sans text-[12.5px] text-[var(--color-ink-muted)] underline-offset-4 hover:text-[var(--color-primary)] hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
                {error && <InlineAlert variant="danger">{error}</InlineAlert>}
                <Button type="submit" variant="primary" size="lg" isLoading={isLoading} className="w-full">
                  Continue
                </Button>
              </form>
            </>
          )}

          {step === 'code' && (
            <>
              <p className="eyebrow mb-3">Verification</p>
              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
                Check Your Inbox.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                {info ?? 'We sent a one-time code to your email. Enter it below to sign in.'}
              </p>

              <form onSubmit={handleCode} noValidate className="mt-10 space-y-6">
                <Input
                  label="Verification code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={10}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
                  disabled={isLoading}
                  autoFocus
                  required
                />

                <label className="flex cursor-pointer items-center gap-3 font-sans text-[13.5px] text-[var(--color-ink-muted)]">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 cursor-pointer rounded-[3px] border border-[var(--color-rule-strong)] accent-[var(--color-primary)]"
                  />
                  Trust this device for the next 30 days
                </label>

                {/* Timer + resend gate. Until the OTP expires we show a
                    monotone countdown; once it hits 0 we surface a
                    "Resend code" button. */}
                <div
                  aria-live="polite"
                  className="flex items-center justify-between gap-3 rounded-[3px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] px-3 py-2.5"
                >
                  {secondsLeft > 0 ? (
                    <>
                      <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-[var(--color-ink-soft)]">
                        Code expires in{' '}
                        <span className="normal-case tracking-normal text-[13.5px] text-[var(--color-ink)]">
                          {formatRemaining(secondsLeft)}
                        </span>
                      </p>
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        title="Wait until the current code expires before requesting a new one."
                        className="cursor-not-allowed font-sans text-[12.5px] text-[var(--color-ink-soft)]"
                      >
                        Resend in {formatRemaining(secondsLeft)}
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-[var(--color-danger-ink)]">
                        Code expired
                      </p>
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={isResending}
                        className="cursor-pointer font-sans text-[12.5px] font-semibold text-[var(--color-primary)] underline-offset-4 hover:underline disabled:cursor-wait disabled:opacity-60"
                      >
                        {isResending ? 'Sending…' : 'Resend code'}
                      </button>
                    </>
                  )}
                </div>

                {error && <InlineAlert variant="danger">{error}</InlineAlert>}

                <Button type="submit" variant="primary" size="lg" isLoading={isLoading} className="w-full">
                  Sign in
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('credentials');
                    setCode('');
                    setChallengeId(null);
                    setExpiresAt(null);
                    setError(null);
                  }}
                  className="block w-full cursor-pointer text-center font-sans text-[13px] text-[var(--color-ink-muted)] underline-offset-4 hover:text-[var(--color-primary)] hover:underline"
                >
                  ← Back
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
